/**
 * THE SEVEN HUE FAMILIES, as a type — ADR-0101 §1 (spec "Citrus Night").
 *
 * One hue, one meaning, and the table is short enough to learn:
 *
 *   lime    valid, "make it valid"   Format (with lemon), Valid JSON, the caret
 *   lemon   repaired by the tool     Fix all, Fix, Undo, the repair banner and log
 *   aqua    reshape, information     Compact, notes, the info toast
 *   violet  discover                 Load example, the examples dialog
 *   coral   danger: lost bytes       Clear, errors
 *   amber   changed but still valid  warnings, a heuristic Fix
 *   peri    neutral                  Copy, Paste, the theme control
 *
 * Colour is never the only channel: severity is always shape + word + tone (P7), and every
 * control that carries a hue also carries a label. The hue is the third channel, not the first.
 *
 * ═══ WHY THIS IS A `.ts` FILE WITH NOTHING BUT A TYPE AND A LIST IN IT ═══
 *
 * The same reason `iconName.ts` is one. `Command` in `src/features/types.ts` names a hue, and
 * that file is compiled by `tsconfig.core.json` — no DOM, no JSX — so the union may not live in
 * a `.tsx`, and it may not live in `src/features/` either, because `ui/**` may not import a
 * feature (gate 2). The presentation layer owns the vocabulary; a feature merely picks a word
 * from it, and the type checker keeps the two spellings from ever drifting apart.
 *
 * The CSS half of the contract is an ATTRIBUTE: a control renders `data-hue="coral"` and its
 * stylesheet keys `[data-hue='coral']` onto the matching tokens (`--coral`, `--coral-sub`,
 * `--coral-line`, `--coral-fill`, `--on-coral`, `--coral-glow`). An attribute survives CSS
 * Modules' class hashing, where a `.coral` class would not, and it is visible in the DOM for
 * the e2e suite and for a user's own stylesheet without being a test-only hook.
 */
export type Hue = 'lime' | 'lemon' | 'aqua' | 'violet' | 'coral' | 'amber' | 'peri';

/**
 * Every hue, in the order of the table above. For exhaustive checks — a test that walks the
 * families, a stylesheet audit that expects one `[data-hue]` block per name — so that "all
 * seven" is a value the compiler holds rather than a number somebody remembers.
 */
export const HUES: readonly Hue[] = ['lime', 'lemon', 'aqua', 'violet', 'coral', 'amber', 'peri'];

/** The hue a control wears when its caller names none: the neutral family. */
export const DEFAULT_HUE: Hue = 'peri';
